import * as React from "react";
import { Alert, Box, Button, Checkbox, Collapse, FormControlLabel, Stack, Typography } from "@mui/material";
import { CaretDownIcon } from "@phosphor-icons/react/dist/ssr/CaretDown";
import { CaretRightIcon } from "@phosphor-icons/react/dist/ssr/CaretRight";

import { useRateCard } from "@/hooks/api/use-rate-card";

import { formatPrice, SECTIONS } from "../rate-card/format";

/**
 * The rate card on the request form: the optional services as tick boxes, and the rest of the
 * card for reference, folded away until asked for.
 */
export function RateCardSection({ value, onChange }: { value: string[]; onChange: (codes: string[]) => void }) {
	const { data: items, isLoading, isError } = useRateCard();
	const [showCard, setShowCard] = React.useState(false);

	if (isLoading) return null;
	if (isError || !items) return <Alert severity="info">The rate card is not available right now.</Alert>;

	const optional = items.filter((item) => item.optional);
	const toggle = (code: string, checked: boolean) =>
		onChange(checked ? [...value, code] : value.filter((picked) => picked !== code));

	return (
		<Stack spacing={1}>
			<Typography variant="subtitle2">Optional services</Typography>
			<Typography variant="body2" color="text.secondary">
				Tick anything you would like us to add. Prices are in SGD and exclude GST.
			</Typography>
			<Stack sx={{ border: "1px solid var(--mui-palette-divider)", borderRadius: 1, px: 2, py: 1 }}>
				{optional.map((item) => (
					<FormControlLabel
						key={item.code}
						sx={{ alignItems: "flex-start", py: 0.5, mr: 0 }}
						control={
							<Checkbox
								checked={value.includes(item.code)}
								onChange={(event) => toggle(item.code, event.target.checked)}
								sx={{ pt: 0.25 }}
							/>
						}
						label={
							<Box>
								<Typography variant="body2">
									{item.name}{" "}
									<Typography component="span" variant="body2" color="text.secondary">
										· {formatPrice(item)}
									</Typography>
								</Typography>
								{item.description ? (
									<Typography variant="caption" color="text.secondary">
										{item.description}
									</Typography>
								) : null}
							</Box>
						}
					/>
				))}
			</Stack>
			<Box>
				<Button
					size="small"
					startIcon={showCard ? <CaretDownIcon /> : <CaretRightIcon />}
					onClick={() => setShowCard((shown) => !shown)}
				>
					{showCard ? "Hide the rate card" : "See the full rate card"}
				</Button>
			</Box>
			<Collapse in={showCard}>
				<Stack spacing={2} sx={{ border: "1px solid var(--mui-palette-divider)", borderRadius: 1, p: 2 }}>
					{SECTIONS.map((section) => {
						const lines = items.filter((item) => item.section === section.value);
						if (lines.length === 0) return null;
						return (
							<Box key={section.value}>
								<Typography variant="subtitle2">{section.label}</Typography>
								<Typography variant="caption" color="text.secondary">
									{section.note}
								</Typography>
								{lines.map((item) => (
									<Stack key={item.code} direction="row" spacing={2} sx={{ justifyContent: "space-between", mt: 0.5 }}>
										<Typography variant="body2">{item.name}</Typography>
										<Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
											{formatPrice(item)}
										</Typography>
									</Stack>
								))}
							</Box>
						);
					})}
				</Stack>
			</Collapse>
		</Stack>
	);
}
